import axios from "axios";

import logger from '../utils/logger';

// API URL configuration
const isDevelopment = process.env.NODE_ENV === "development";
const API_VERSION = "/api/v1";

// Market API base - dev uses proxy, prod uses /market behind Nginx
export const MARKET_BASE = process.env.REACT_APP_MARKET_BASE_URL || '/market';
export const MARKET_DATA_URL = `${MARKET_BASE}/market-data`;

// Only use environment override if it's explicitly allowed
const ENV_BASE = (() => {
  // Only honor build-time override if explicitly allowed
  const allowEnv =
    typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_ALLOW_ENV_BASE === "1";
  if (allowEnv &&
      typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_API_BASE_URL) {
    const envUrl = process.env.REACT_APP_API_BASE_URL.trim();
    // Safety check: don't use demoquanto or any external domain unless explicitly intended
    if (envUrl && !envUrl.includes('demoquanto') && !envUrl.includes('localhost')) {
      return envUrl;
    }
  }
  return undefined;
})();

// Runtime-safe fallback for production (works on CloudFront)
const RUNTIME_BASE = (typeof window !== "undefined" && window.location)
  ? window.location.origin // defaults to same origin
  : ""; // SSR/CI – still fine

// Create axios instance with default config
const api = axios.create({
  // Dev: use relative URLs (proxy handles routing); Prod: use ENV override if safe, else same origin
  baseURL: isDevelopment
    ? API_VERSION  // Empty base in dev - proxy handles routing
    : `${ENV_BASE ?? RUNTIME_BASE}${API_VERSION}`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    // Log request details for debugging
    logger.info("API Request:", {
      url: config.url,
      method: config.method,
      headers: config.headers,
      baseURL: config.baseURL,
    });
    return config;
  },
  (error) => {
    logger.error("Request error:", error);
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful response for debugging
    logger.info("API Response:", {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    // Only log non-404 errors
    if (error.response?.status !== 404) {
      logger.error("API Error:", {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
    }

    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem("token");
      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

export default api;

// Export configuration for legacy compatibility
export const apiConfig = {
  baseUrl: isDevelopment ? "" : (ENV_BASE ?? RUNTIME_BASE),  // Empty in dev - proxy handles routing
  versionPrefix: API_VERSION,
  getFullUrl: (path) =>
    isDevelopment
      ? `${API_VERSION}${path}`  // Relative URL in dev
      : `${ENV_BASE ?? RUNTIME_BASE}${API_VERSION}${path}`,
  api,
  // Security info for debugging
  security: {
    envOverrideAllowed: process.env.REACT_APP_ALLOW_ENV_BASE === "1",
    envBaseUrl: ENV_BASE,
    runtimeBase: RUNTIME_BASE,
    isDevelopment
  }
};

// --- Holdings API ---

/**
 * Fetch holdings for an account (optionally filter by account_number, symbol, etc.)
 * @param {Object} params - Query params (e.g., { account_number })
 * @returns {Promise}
 */
export const getHoldings = async (params = {}) => {
  return api.get("/advisor-portal/holdings", { params });
};

/**
 * Fetch holdings history for an account
 * @param {string} account_number
 * @returns {Promise}
 */
export const getHoldingsHistory = async (account_number) => {
  return api.get(`/advisor-portal/holdings/history/${account_number}`);
};

/**
 * Fetch all security types
 * @returns {Promise}
 */
export const getSecurityTypes = async () => {
  return api.get("/advisor-portal/holdings/security-types");
};

/**
 * Update the holdings data file with latest market data
 * @returns {Promise}
 */
export const updateHoldingsData = async () => {
  try {
    const response = await api.post("/advisor-portal/holdings/update-data");
    
    // Store the last update time and next update due date
    localStorage.setItem('holdingsLastUpdate', response.data.lastUpdate);
    localStorage.setItem('holdingsNextUpdate', response.data.nextUpdateDue);
    
    return response.data;
  } catch (error) {
    logger.error("Failed to update holdings data:", error);
    throw error;
  }
};
